/**
 * @fullvsl/app-shell/styles — shell CSS for the integrated MVP app (FUL-225/226).
 *
 * FullVSL design system (approved Google Stitch export, DESIGN.md):
 * - Primary teal #00685f / primary-container #008378.
 * - Inter typography, JetBrains Mono for data/IDs/numbers.
 * - Background #f5faf8, surface hierarchy from DESIGN.md tokens.
 * - Outline-variant #bcc9c6 for borders. Tight radii (4–8px).
 * - Fixed 240px sidebar, fixed 56px topbar with search.
 * - Dense data tables, KPI cards with mono values, no heavy shadows.
 * Responsive: sidebar collapses to a slide-over drawer on mobile.
 * Optimized for the Full Funnel iframe constraint (no horizontal scroll).
 */

export const SHELL_CSS = `
:root {
  /* ---- Color tokens (DESIGN.md) ---- */
  --fv-primary: #00685f;
  --fv-primary-container: #008378;
  --fv-on-primary: #ffffff;
  --fv-on-primary-container: #f4fffc;
  --fv-primary-dark: #004a44;
  --fv-primary-tint: #d7f5ee;

  --fv-bg: #f5faf8;
  --fv-surface: #ffffff;
  --fv-surface-dim: #d6dbd9;
  --fv-surface-container-low: #f0f5f2;
  --fv-surface-container: #eaefed;
  --fv-surface-container-high: #e4e9e7;
  --fv-surface-container-highest: #dee4e1;

  --fv-on-surface: #171d1c;
  --fv-on-surface-variant: #3d4947;
  --fv-outline: #6d7a77;
  --fv-outline-variant: #bcc9c6;
  --fv-border: var(--fv-outline-variant);
  --fv-border-light: #d8e0dc;

  --fv-text: var(--fv-on-surface);
  --fv-text-secondary: var(--fv-on-surface-variant);
  --fv-text-muted: var(--fv-outline);

  --fv-error: #ba1a1a;
  --fv-success: #15803d;
  --fv-warning: #b45309;

  /* ---- Radii (DESIGN.md) ---- */
  --fv-radius-sm: 4px;
  --fv-radius: 6px;
  --fv-radius-lg: 8px;
  --fv-radius-xl: 12px;

  /* ---- Layout ---- */
  --fv-sidebar-w: 240px;
  --fv-topbar-h: 56px;
  --fv-shadow-1: 0 1px 3px rgba(0,0,0,.04);
  --fv-shadow-2: 0 4px 12px rgba(0,0,0,.05);
  color-scheme: light;
}

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; height: 100%; }
body {
  background: var(--fv-bg);
  color: var(--fv-text);
  font: 14px/1.5 "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.fva-mono { font-family: "JetBrains Mono", ui-monospace, "SF Mono", monospace; }

/* ============ SHELL LAYOUT ============ */
.fva-shell { min-height: 100vh; width: 100%; max-width: 100vw; overflow-x: hidden; display: flex; flex-direction: row; }

/* ---- Sidebar ---- */
.fva-sidebar {
  width: var(--fv-sidebar-w);
  flex: 0 0 var(--fv-sidebar-w);
  background: var(--fv-surface);
  border-right: 1px solid var(--fv-outline-variant);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}
.fva-brand {
  padding: 20px 24px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.fva-brand-logo {
  width: 32px; height: 32px;
  border-radius: var(--fv-radius-lg);
  background: var(--fv-primary);
  color: var(--fv-on-primary);
  display: flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
}
.fva-brand-logo svg { width: 20px; height: 20px; }
.fva-brand-name { font-weight: 700; font-size: 17px; line-height: 1.15; color: var(--fv-primary); }
.fva-brand-sub { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: var(--fv-text-muted); font-weight: 700; opacity: .6; }

.fva-nav { padding: 4px 0; flex: 1 1 auto; }
.fva-nav-section-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: .08em;
  color: var(--fv-text-muted); font-weight: 700;
  padding: 14px 24px 4px; opacity: .6;
}
.fva-nav-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 24px;
  color: var(--fv-text-secondary);
  text-decoration: none;
  font-size: 14px; font-weight: 500;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  border-right: 3px solid transparent;
  transition: background .12s ease, color .12s ease;
}
.fva-nav-item:hover { background: var(--fv-surface-container-low); color: var(--fv-text); }
.fva-nav-item.is-active {
  background: var(--fv-surface-container-low);
  color: var(--fv-primary);
  font-weight: 700;
  border-right-color: var(--fv-primary);
}
.fva-nav-icon { width: 20px; height: 20px; flex: 0 0 auto; opacity: .7; }
.fva-nav-item.is-active .fva-nav-icon { opacity: 1; }

.fva-nav-cta {
  margin: 8px 16px;
  padding: 9px 16px;
  border-radius: var(--fv-radius-sm);
  background: var(--fv-primary);
  color: var(--fv-on-primary);
  font: 600 13px/1 "Inter", sans-serif;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: opacity .12s ease;
}
.fva-nav-cta:hover { opacity: .9; }
.fva-nav-cta svg { width: 18px; height: 18px; }

.fva-sidebar-footer {
  padding: 12px 0 20px;
  border-top: 1px solid var(--fv-border-light);
}
.fva-sidebar-footer .fva-nav-item { padding: 7px 24px; font-size: 13px; }
.fva-sidebar-footer .fva-nav-item:hover { color: var(--fv-primary); }

/* ---- Main + topbar ---- */
.fva-main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }

.fva-topbar {
  display: flex; align-items: center; gap: 16px;
  height: var(--fv-topbar-h);
  padding: 0 24px;
  background: var(--fv-surface);
  border-bottom: 1px solid var(--fv-outline-variant);
  position: sticky; top: 0; z-index: 10;
}
.fva-topbar-search {
  position: relative; flex: 1 1 auto; max-width: 420px;
}
.fva-topbar-search svg {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  width: 18px; height: 18px; color: var(--fv-text-muted);
}
.fva-topbar-search input {
  width: 100%; height: 36px;
  padding: 0 12px 0 36px;
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-sm);
  font: 14px "Inter", sans-serif; color: var(--fv-text);
  transition: border-color .12s ease, box-shadow .12s ease;
}
.fva-topbar-search input:focus {
  outline: none; border-color: var(--fv-primary-container);
  box-shadow: 0 0 0 2px rgba(0,131,120,.12);
}
.fva-topbar-search input::placeholder { color: var(--fv-text-muted); }

.fva-topbar-title {
  font-size: 14px; font-weight: 600; margin: 0; flex: 1 1 auto;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fva-topbar-actions { display: flex; align-items: center; gap: 12px; flex: 0 0 auto; }
.fva-icon-btn {
  width: 36px; height: 36px;
  border: 1px solid transparent; background: none;
  border-radius: var(--fv-radius-sm);
  cursor: pointer; color: var(--fv-text-secondary);
  display: inline-flex; align-items: center; justify-content: center;
  position: relative; transition: background .12s ease, color .12s ease;
}
.fva-icon-btn:hover { background: var(--fv-surface-container-low); color: var(--fv-text); }
.fva-icon-btn svg { width: 20px; height: 20px; }
.fva-icon-btn-dot {
  position: absolute; top: 7px; right: 8px;
  width: 8px; height: 8px; border-radius: 50%; background: var(--fv-error);
  border: 2px solid var(--fv-surface);
}
.fva-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: var(--fv-surface-container-high);
  border: 1px solid var(--fv-outline-variant);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: var(--fv-text-secondary);
}
.fva-topbar-back {
  font-size: 13px; color: var(--fv-primary); cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
  border: none; background: none; font-weight: 600;
}
.fva-topbar-back:hover { text-decoration: underline; }

.fva-content { padding: 24px; flex: 1 1 auto; overflow-y: auto; }
.fva-content-inner { max-width: 1440px; margin: 0 auto; }

/* ---- Mobile sidebar toggle ---- */
.fva-menu-btn {
  display: none;
  border: 1px solid var(--fv-outline-variant);
  background: var(--fv-surface);
  border-radius: var(--fv-radius-sm);
  width: 36px; height: 36px;
  cursor: pointer;
  font-size: 18px; color: var(--fv-text);
}

/* ============ PAGE HEADER ============ */
.fva-page-header {
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
}
.fva-page-header h2 { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -.01em; color: var(--fv-text); }
.fva-page-header p { margin: 4px 0 0; color: var(--fv-text-muted); font-size: 14px; }
.fva-page-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

/* ============ BUTTONS ============ */
.fva-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px;
  border-radius: var(--fv-radius-sm);
  font: 600 13px/1 "Inter", system-ui, sans-serif;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  height: 36px;
  transition: background .12s ease, border-color .12s ease, opacity .12s ease;
  white-space: nowrap;
}
.fva-btn svg { width: 16px; height: 16px; flex: 0 0 auto; }
.fva-btn-primary { background: var(--fv-primary); color: var(--fv-on-primary); }
.fva-btn-primary:hover { opacity: .9; }
.fva-btn-secondary {
  background: var(--fv-surface); color: var(--fv-text);
  border-color: var(--fv-outline-variant);
}
.fva-btn-secondary:hover { background: var(--fv-surface-container-low); }
.fva-btn-danger { background: transparent; color: var(--fv-error); border-color: var(--fv-outline-variant); }
.fva-btn-danger:hover { background: #fef2f2; border-color: var(--fv-error); }
.fva-btn-sm { padding: 5px 12px; font-size: 12px; height: auto; }

/* ============ CARDS / SURFACES ============ */
.fva-card {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
}
.fva-card-pad { padding: 20px; }

/* ============ KPI CARDS ============ */
.fva-kpi-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.fva-kpi-card {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
  padding: 20px;
}
.fva-kpi-label {
  font-size: 12px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .05em; color: var(--fv-text-secondary); margin: 0 0 8px;
}
.fva-kpi-value {
  font: 500 28px/1 "JetBrains Mono", ui-monospace, monospace;
  color: var(--fv-primary); margin: 0;
}
.fva-kpi-delta {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 12px; font-weight: 700; margin-left: 8px;
}
.fva-kpi-delta.up { color: #059669; }
.fva-kpi-delta.down { color: #e11d48; }
.fva-kpi-delta svg { width: 14px; height: 14px; }

/* ============ DATA TABLE ============ */
.fva-table-wrap {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
  overflow: hidden;
}
.fva-table-scroll { overflow-x: auto; }
.fva-table {
  width: 100%; border-collapse: collapse; text-align: left;
}
.fva-table thead th {
  background: var(--fv-surface-container-low);
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: var(--fv-text-secondary);
  padding: 12px 20px;
  border-bottom: 1px solid var(--fv-outline-variant);
  white-space: nowrap;
}
.fva-table thead th.num { text-align: right; }
.fva-table thead th.center { text-align: center; }
.fva-table tbody tr {
  border-bottom: 1px solid var(--fv-border-light);
  transition: background .1s ease;
}
.fva-table tbody tr:last-child { border-bottom: none; }
.fva-table tbody tr:hover { background: var(--fv-surface-container-low); }
.fva-table td { padding: 12px 20px; font-size: 14px; vertical-align: middle; }
.fva-table td.num { text-align: right; font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 13px; }
.fva-table td.center { text-align: center; }

.fva-table-name { display: flex; align-items: center; gap: 12px; }
.fva-table-thumb {
  width: 48px; height: 32px; border-radius: var(--fv-radius-sm);
  background: linear-gradient(135deg, var(--fv-primary) 0%, var(--fv-primary-dark) 100%);
  flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,.6); font-size: 14px;
}
.fva-table-thumb.is-draft { background: var(--fv-surface-container-high); color: var(--fv-text-muted); }
.fva-table-name-text { min-width: 0; }
.fva-table-name-text p { margin: 0; font-weight: 600; color: var(--fv-text); }
.fva-table-name-text span { font-size: 11px; color: var(--fv-text-muted); }

.fva-badge {
  display: inline-flex; align-items: center;
  font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: var(--fv-radius-sm);
  text-transform: uppercase; letter-spacing: .03em;
}
.fva-badge-live { background: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }
.fva-badge-draft { background: var(--fv-surface-container-high); color: var(--fv-text-secondary); border: 1px solid var(--fv-outline-variant); }

.fva-progress-mini {
  display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
}
.fva-progress-mini .pct { font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 13px; }
.fva-progress-bar {
  width: 64px; height: 4px;
  background: var(--fv-surface-container-highest);
  border-radius: 999px; overflow: hidden;
}
.fva-progress-bar > span { display: block; height: 100%; background: var(--fv-primary); border-radius: 999px; }

.fva-table-footer {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 20px;
  border-top: 1px solid var(--fv-outline-variant);
  background: var(--fv-surface);
  font-size: 12px; color: var(--fv-text-muted);
}

/* ============ VSL CARD GRID (empty state fallback) ============ */
.fva-vsl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.fva-vsl-card {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: border-color .12s ease;
}
.fva-vsl-card:hover { border-color: var(--fv-primary); }
.fva-vsl-thumb {
  height: 120px;
  background: linear-gradient(135deg, var(--fv-primary) 0%, var(--fv-primary-dark) 100%);
  display: flex; align-items: center; justify-content: center;
  position: relative;
}
.fva-vsl-thumb-icon { color: rgba(255,255,255,.5); font-size: 36px; }
.fva-vsl-body { padding: 14px 16px; }
.fva-vsl-name { font-size: 15px; font-weight: 600; margin: 0 0 4px; }
.fva-vsl-meta { font-size: 12px; color: var(--fv-text-muted); margin: 0; }

/* ============ TABS ============ */
.fva-tabs {
  display: flex; gap: 0; border-bottom: 1px solid var(--fv-outline-variant);
  margin-bottom: 20px; max-width: 100%; overflow-x: auto; overflow-y: hidden;
}
.fva-tab {
  flex: 0 0 auto; white-space: nowrap;
  padding: 10px 16px;
  font: 600 13px/1 "Inter", sans-serif;
  color: var(--fv-text-secondary);
  background: none; border: none; cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color .12s ease, border-color .12s ease;
}
.fva-tab:hover { color: var(--fv-text); }
.fva-tab.is-active { color: var(--fv-primary); border-bottom-color: var(--fv-primary); }

/* ============ FORMS ============ */
.fva-form { width: 100%; max-width: 760px; min-width: 0; }
.fva-form-section { margin-bottom: 28px; }
.fva-form-section-title {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: var(--fv-text-secondary);
  margin: 0 0 14px; padding-bottom: 8px; border-bottom: 1px solid var(--fv-border-light);
}
.fva-field { margin-bottom: 16px; }
.fva-label {
  display: block; font-size: 12px; font-weight: 600;
  color: var(--fv-text-secondary); margin-bottom: 4px;
}
.fva-label-hint { font-size: 11px; color: var(--fv-text-muted); font-weight: 400; margin-left: 6px; }
.fva-input, .fva-select, .fva-textarea {
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-sm);
  font: 14px/1.4 "Inter", system-ui, sans-serif;
  color: var(--fv-text);
  background: var(--fv-surface);
  transition: border-color .12s ease, box-shadow .12s ease;
}
.fva-textarea { height: auto; min-height: 64px; padding: 8px 10px; resize: vertical; }
.fva-input:focus, .fva-select:focus, .fva-textarea:focus {
  outline: none; border-color: var(--fv-primary-container);
  box-shadow: 0 0 0 2px rgba(0,131,120,.12);
}
.fva-progress-curve-control { display: flex; align-items: center; gap: 12px; }
.fva-progress-curve-control input[type="range"] { flex: 1 1 auto; accent-color: var(--fv-primary); }
.fva-progress-curve-value { min-width: 34px; font: 600 12px/1.2 "JetBrains Mono", ui-monospace, monospace; color: var(--fv-text-secondary); text-align: right; }
.fva-progress-curve-preview { margin: -4px 0 16px; padding: 9px 11px; border-left: 3px solid var(--fv-primary-container); background: var(--fv-surface-container); color: var(--fv-text-secondary); font-size: 12px; }
.fva-field-row { display: flex; gap: 12px; }
.fva-field-row > .fva-field { flex: 1 1 0; }
.fva-radio-group, .fva-check-group { display: flex; flex-wrap: wrap; gap: 8px; }
.fva-radio {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-sm);
  font-size: 13px; cursor: pointer;
  background: var(--fv-surface);
  transition: border-color .12s ease, background .12s ease;
}
.fva-radio:hover { border-color: var(--fv-primary); }
.fva-radio.is-checked { border-color: var(--fv-primary); background: var(--fv-primary-tint); font-weight: 600; }
.fva-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }

/* ---- Aspect ratio tiles ---- */
.fva-ratio-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 10px; }
.fva-ratio-tile {
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-sm);
  padding: 10px 8px;
  text-align: center;
  cursor: pointer;
  background: var(--fv-surface);
  transition: border-color .12s ease, background .12s ease;
}
.fva-ratio-tile:hover { border-color: var(--fv-primary); }
.fva-ratio-tile.is-checked { border: 2px solid var(--fv-primary); background: var(--fv-primary-tint); padding: 9px 7px; }
.fva-ratio-tile-shape { width: 100%; height: 28px; display: flex; align-items: center; justify-content: center; margin-bottom: 4px; }
.fva-ratio-tile-shape span { display: block; background: var(--fv-text-muted); border-radius: 2px; }
.fva-ratio-tile.is-checked .fva-ratio-tile-shape span { background: var(--fv-primary); }
.fva-ratio-tile-label { font-size: 11px; font-weight: 600; color: var(--fv-text-secondary); }

/* ---- Sticky form action bar ---- */
.fva-action-bar {
  display: flex; gap: 10px; align-items: center;
  padding-top: 20px; margin-top: 8px;
  border-top: 1px solid var(--fv-border-light);
}

/* ============ PLAYER PREVIEW ============ */
.fva-preview-layout { display: flex; gap: 20px; flex-wrap: wrap; }
.fva-preview-player { flex: 1 1 480px; min-width: 0; }
.fva-preview-side { flex: 1 1 300px; min-width: 0; }
.fva-preview-panel {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
  padding: 16px;
  margin-bottom: 16px;
}
.fva-preview-panel-title {
  font-size: 12px; font-weight: 700; margin: 0 0 10px;
  text-transform: uppercase; letter-spacing: .05em; color: var(--fv-text-secondary);
}
.fva-event-log {
  max-height: 220px; overflow-y: auto;
  font: 12px/1.5 "JetBrains Mono", ui-monospace, monospace;
}
.fva-event-row {
  display: flex; gap: 8px; align-items: baseline;
  padding: 2px 0; border-bottom: 1px solid var(--fv-border-light);
}
.fva-event-name { font-weight: 600; color: var(--fv-primary); min-width: 140px; }
.fva-event-meta { color: var(--fv-text-muted); }

/* ---- Embed snippet ---- */
.fva-snippet {
  background: #1c2120; color: #cdd3df;
  border-radius: var(--fv-radius-sm);
  padding: 14px;
  font: 12px/1.6 "JetBrains Mono", ui-monospace, monospace;
  overflow-x: auto; white-space: pre;
}
.fva-snippet-copy {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: var(--fv-primary);
  cursor: pointer; border: none; background: none;
  margin-bottom: 6px; font-weight: 600;
}

/* ============ ANALYTICS ============ */
.fva-analytics-wrap {
  width: 100%; min-width: 0;
}
.fva-an-page { display: flex; flex-direction: column; gap: 16px; }
.fva-data-badge {
  align-self: flex-start; padding: 5px 9px; border-radius: var(--fv-radius-sm);
  background: var(--fv-surface-container-high); color: var(--fv-text-secondary);
  font-size: 11px; font-weight: 700;
}
.fva-data-badge.is-live { background: var(--fv-success-container); color: #005049; }
.fva-an-header-actions { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
.fva-an-period { display: flex; flex-direction: column; gap: 4px; }
.fva-an-period > span { font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--fv-text-muted); }
.fva-an-period .fva-select { width: auto; min-width: 156px; height: 34px; padding: 5px 32px 5px 10px; font-size: 12px; font-weight: 600; }
.fva-an-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.fva-an-kpi {
  min-width: 0; background: var(--fv-surface); border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg); padding: 14px 16px;
}
.fva-an-kpi-head, .fva-an-kpi-body { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.fva-an-kpi-label { margin: 0; font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--fv-text-secondary); }
.fva-an-kpi-icon { width: 18px; height: 18px; color: var(--fv-primary); }
.fva-an-kpi-value { margin: 7px 0 2px; font: 700 24px/1.2 "JetBrains Mono", ui-monospace, monospace; color: var(--fv-text); }
.fva-an-delta { display: block; font-size: 10px; white-space: nowrap; }
.fva-an-delta.is-up { color: #08765f; }
.fva-an-delta.is-down { color: var(--fv-error); }
.fva-an-spark { width: 82px; height: 34px; align-self: flex-end; overflow: visible; }
.fva-an-spark path { fill: none; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
.fva-an-spark path.is-positive { stroke: var(--fv-primary); }
.fva-an-spark path.is-negative { stroke: var(--fv-error); }
.fva-an-main-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr); gap: 12px; }
.fva-an-mix-grid { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(300px, .65fr); gap: 12px; }
.fva-an-card, .fva-an-ranking {
  min-width: 0; background: var(--fv-surface); border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg); padding: 16px 18px;
}
.fva-an-card h3, .fva-an-section-head h3 { margin: 0; font-size: 15px; font-weight: 700; color: var(--fv-text); }
.fva-an-card-sub, .fva-an-section-head p { margin: 3px 0 0; font-size: 12px; color: var(--fv-text-muted); }
.fva-an-legend { display: flex; justify-content: flex-end; gap: 14px; margin-top: -22px; }
.fva-an-legend span { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: var(--fv-text-secondary); }
.fva-an-legend i { display: inline-block; width: 12px; }
.fva-an-legend i.is-bar { height: 8px; border-radius: 2px; background: rgba(0,104,95,.22); }
.fva-an-legend i.is-line { height: 2px; background: #d97706; }
.fva-an-chart { width: 100%; height: 270px; margin-top: 10px; }
.fva-an-chart svg { display: block; width: 100%; height: 100%; overflow: visible; }
.fva-an-grid-line { stroke: var(--fv-surface-container-high); stroke-width: 1; }
.fva-an-bar-rect { fill: rgba(0,104,95,.22); }
.fva-an-bar-rect:hover { fill: rgba(0,104,95,.38); }
.fva-an-rate-line { fill: none; stroke: #d97706; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
.fva-an-axis { fill: var(--fv-text-muted); font: 10px "JetBrains Mono", ui-monospace, monospace; text-anchor: middle; }
.fva-an-funnel { display: flex; flex-direction: column; gap: 14px; margin-top: 18px; }
.fva-an-funnel-step { display: flex; flex-direction: column; gap: 6px; }
.fva-an-funnel-head { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; }
.fva-an-funnel-head strong { font-family: "JetBrains Mono", ui-monospace, monospace; }
.fva-an-funnel-track, .fva-an-source-track { height: 8px; overflow: hidden; border-radius: 999px; background: var(--fv-surface-container-high); }
.fva-an-funnel-track span, .fva-an-source-track span { display: block; height: 100%; border-radius: inherit; }
.fva-an-funnel-step small { font-size: 10px; color: var(--fv-text-muted); }
.fva-an-source-list { display: flex; flex-direction: column; gap: 13px; margin-top: 16px; }
.fva-an-source-row { display: flex; flex-direction: column; gap: 6px; }
.fva-an-source-head, .fva-an-device-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-size: 12px; }
.fva-an-source-head span, .fva-an-device-row span { display: inline-flex; align-items: center; gap: 8px; }
.fva-an-source-head strong, .fva-an-device-row strong { font: 600 11px/1.3 "JetBrains Mono", ui-monospace, monospace; white-space: nowrap; }
.fva-an-swatch { display: inline-block; width: 9px; height: 9px; border-radius: 2px; flex: 0 0 auto; }
.fva-an-device-body { display: flex; align-items: center; justify-content: center; gap: 28px; min-height: 180px; }
.fva-an-donut {
  width: 132px; height: 132px; border-radius: 50%; flex: 0 0 auto;
  display: grid; place-items: center;
  background: conic-gradient(#00685f 0 62%, #7ba8a2 62% 93%, #d97706 93% 100%);
}
.fva-an-donut-center {
  width: 78px; height: 78px; border-radius: 50%; background: var(--fv-surface);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.fva-an-donut-center strong { font: 700 19px/1.2 "JetBrains Mono", ui-monospace, monospace; }
.fva-an-donut-center span { font-size: 10px; color: var(--fv-text-muted); }
.fva-an-device-legend { display: flex; flex: 1 1 auto; flex-direction: column; gap: 14px; min-width: 0; }
.fva-an-section-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.fva-an-count { padding: 4px 8px; border-radius: var(--fv-radius-sm); background: var(--fv-surface-container-high); font-size: 11px; color: var(--fv-text-secondary); }
.fva-an-ranking { padding: 0; overflow: hidden; }
.fva-an-ranking .fva-an-section-head { padding: 16px 18px 0; }
.fva-an-table { min-width: 820px; }
.fva-an-table tbody tr { cursor: pointer; }
.fva-an-footnote { margin: 0; padding: 10px 18px; border-top: 1px solid var(--fv-border-light); font-size: 11px; color: var(--fv-text-muted); }
.fva-an-detail-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.fva-an-detail-actions { display: flex; gap: 8px; }

/* Detailed analytics dashboard */
.fv-dash { width: 100%; margin: 0; }
.fv-header { display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
.fv-title { font-size: 22px; line-height: 1.25; font-weight: 700; margin: 0; color: var(--fv-text); }
.fv-sub { margin: 4px 0 0; color: var(--fv-text-secondary); font-size: 12px; }
.fv-badge { display: inline-block; margin-top: 8px; font-size: 10px; font-weight: 700; padding: 3px 7px; border-radius: var(--fv-radius-sm); }
.fv-badge-fixture { background: var(--fv-surface-container-high); color: var(--fv-text-secondary); }
.fv-badge-live { background: var(--fv-success-container); color: #005049; }
.fv-legend { display: flex; gap: 14px; align-items: center; }
.fv-key { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: var(--fv-text-secondary); }
.fv-dot { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
.fv-dot-primary { background: var(--fv-primary); }
.fv-dot-cta { background: #d97706; }
.fv-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.fv-kpi { background: var(--fv-surface); border: 1px solid var(--fv-outline-variant); border-radius: var(--fv-radius-lg); padding: 12px 14px; }
.fv-kpi-accent { border-left: 4px solid var(--fv-primary); }
.fv-kpi-label { margin: 0 0 4px; font-size: 10px; text-transform: uppercase; color: var(--fv-text-secondary); font-weight: 700; }
.fv-kpi-value { margin: 0; font: 700 20px/1.25 "JetBrains Mono", ui-monospace, monospace; color: var(--fv-text); }
.fv-kpi-hint { margin: 2px 0 0; font-size: 10px; color: var(--fv-text-muted); }
.fv-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.fv-grid-two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.fv-card { background: var(--fv-surface); border: 1px solid var(--fv-outline-variant); border-radius: var(--fv-radius-lg); padding: 16px 18px; grid-column: span 12; }
.fv-card-wide { grid-column: span 12; }
.fv-card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--fv-text); }
.fv-card-sub { margin: 0 0 12px; font-size: 11px; color: var(--fv-text-muted); }
.fv-grid > .fv-card-wide:nth-child(1) { grid-column: span 8; }
.fv-grid > .fv-bd-col:nth-child(2) { grid-column: span 4; display: flex; flex-direction: column; gap: 12px; }
.fv-bd-col > .fv-card { grid-column: auto; }
.fv-chart { width: 100%; min-height: 240px; }
.fv-retention { width: 100%; height: 260px; overflow: visible; }
.fv-grid line.fv-grid { stroke: var(--fv-surface-container-high); stroke-width: 1; }
.fv-axis { fill: var(--fv-text-muted); font: 10px "JetBrains Mono", ui-monospace, monospace; text-anchor: middle; }
.fv-ret-area { fill: rgba(0,104,95,.09); }
.fv-ret-line { fill: none; stroke: var(--fv-primary); stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
.fv-cta-line { stroke: #d97706; stroke-width: 1.5; stroke-dasharray: 4 4; }
.fv-cta-label { fill: #b45309; font: 600 10px "JetBrains Mono", ui-monospace, monospace; }
.fv-drop-dot { fill: var(--fv-error); }
.fv-note { margin: 10px 0 0; font-size: 11px; padding: 8px 10px; border-radius: var(--fv-radius-sm); }
.fv-note-info { background: var(--fv-surface-container-low); color: #005049; }
.fv-note-warn { background: #fff3e6; color: #92400e; }
.fv-bd-list { display: flex; flex-direction: column; gap: 11px; }
.fv-bd-row { display: flex; flex-direction: column; gap: 5px; }
.fv-bd-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.fv-bd-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.fv-bd-badge { width: 25px; height: 25px; border-radius: var(--fv-radius-sm); background: var(--fv-surface-container-high); color: var(--fv-primary); font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.fv-bd-name { font-size: 12px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fv-bd-meta { font: 11px "JetBrains Mono", ui-monospace, monospace; color: var(--fv-text-secondary); white-space: nowrap; }
.fv-bar { height: 6px; background: var(--fv-surface-container-high); border-radius: 999px; overflow: hidden; }
.fv-bar-fill { height: 100%; background: var(--fv-primary); border-radius: 999px; }
.fv-funnel { display: flex; flex-direction: column; gap: 13px; }
.fv-funnel-step { display: flex; flex-direction: column; gap: 5px; }
.fv-funnel-top { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.fv-funnel-label { font-size: 12px; font-weight: 700; }
.fv-funnel-meta { font-size: 10px; color: var(--fv-text-muted); }
.fv-funnel-count { font: 700 15px "JetBrains Mono", ui-monospace, monospace; }
.fv-resume-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.fv-stat { background: var(--fv-surface-container-low); border-radius: var(--fv-radius-lg); padding: 12px; text-align: center; }
.fv-stat-accent { background: var(--fv-success-container); }
.fv-stat-label { margin: 0 0 4px; font-size: 10px; text-transform: uppercase; color: var(--fv-text-secondary); font-weight: 700; }
.fv-stat-value { margin: 0; font: 700 18px "JetBrains Mono", ui-monospace, monospace; }
.fv-ev-head { display: flex; flex-direction: column; }
.fv-table-wrap { overflow-x: auto; }
.fv-table { width: 100%; min-width: 680px; border-collapse: collapse; font-size: 12px; }
.fv-table th { text-align: left; font-size: 10px; text-transform: uppercase; color: var(--fv-text-secondary); font-weight: 700; padding: 8px 10px; border-bottom: 1px solid var(--fv-outline-variant); }
.fv-table td { padding: 8px 10px; border-bottom: 1px solid var(--fv-border-light); }
.fv-table tbody tr:hover { background: var(--fv-surface-container-low); }
.fv-mono { font-family: "JetBrains Mono", ui-monospace, monospace; color: var(--fv-text-secondary); }
.fv-ev-tag { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: var(--fv-radius-sm); display: inline-block; }
.fv-ev-conv { background: var(--fv-success-container); color: #005049; }
.fv-ev-neutral { background: var(--fv-surface-container-high); color: var(--fv-text-secondary); }
.fv-ev-warn { background: #fff3e6; color: #92400e; }
.fv-loading { padding: 40px 0; text-align: center; color: var(--fv-text-muted); }
.fv-error { color: var(--fv-error); font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 12px; }

/* ============ EMPTY / ERROR STATES ============ */
.fva-empty {
  text-align: center; padding: 48px 24px; color: var(--fv-text-muted);
}
.fva-empty h3 { margin: 0 0 6px; color: var(--fv-text-secondary); }
.fva-empty p { margin: 0; font-size: 13px; }
.fva-error-banner {
  background: #fef2f2; border: 1px solid #fecaca;
  border-radius: var(--fv-radius-sm);
  padding: 10px 14px; color: var(--fv-error);
  font-size: 13px; margin-bottom: 16px;
}
.fva-info-banner {
  background: var(--fv-surface-container-low); border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-sm);
  padding: 10px 14px; color: var(--fv-text-secondary);
  font-size: 12px; margin-bottom: 16px; line-height: 1.5;
}

/* ============ KPI REFINEMENTS ============ */
.fva-kpi-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
.fva-kpi-icon { width: 20px; height: 20px; color: var(--fv-primary); opacity: .8; }
.fva-kpi-valrow { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.fva-kpi-sub { font-size: 12px; font-weight: 600; color: var(--fv-text-muted); opacity: .7; }
.fva-kpi-delta-icon { width: 14px; height: 14px; }
.fva-text-muted { color: var(--fv-text-muted); }
.fva-btn-icon { width: 16px; height: 16px; flex: 0 0 auto; }
.fva-btn-block { width: 100%; justify-content: center; margin-top: 12px; }

/* ============ CONTEXTUAL INSIGHT FOOTER ============ */
.fva-insight-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;
}
.fva-insight-opp {
  background: var(--fv-primary-container);
  color: var(--fv-on-primary-container);
  border-radius: var(--fv-radius-lg);
  padding: 24px;
  position: relative;
  overflow: hidden;
}
.fva-insight-opp-body { position: relative; z-index: 1; }
.fva-insight-title { font-size: 16px; font-weight: 600; margin: 0 0 8px; }
.fva-insight-text { font-size: 14px; line-height: 1.5; opacity: .9; margin: 0 0 16px; max-width: 440px; }
.fva-insight-btn {
  border: none; cursor: pointer;
  background: var(--fv-on-primary-container); color: var(--fv-primary-container);
  font: 700 13px/1 "Inter", sans-serif;
  padding: 9px 16px; border-radius: var(--fv-radius-sm);
  transition: opacity .12s ease;
}
.fva-insight-btn:hover { opacity: .9; }
.fva-insight-bg {
  position: absolute; right: -24px; bottom: -32px;
  width: 140px; height: 140px; opacity: .1;
  transform: rotate(12deg);
}
.fva-insight-act {
  background: var(--fv-surface);
  border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg);
  padding: 24px;
  display: flex; flex-direction: column;
}
.fva-insight-act-head { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
.fva-insight-act-icon { width: 20px; height: 20px; color: var(--fv-primary); }
.fva-insight-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.fva-insight-list-item {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 14px; gap: 12px;
}
.fva-insight-list-text { color: var(--fv-on-surface-variant); }
.fva-insight-list-time { font-size: 11px; color: var(--fv-text-muted); opacity: .7; white-space: nowrap; }

/* ============ PUBLISH VIEW ============ */
.fva-segmented {
  display: inline-flex; background: var(--fv-surface-container-high);
  padding: 3px; border-radius: var(--fv-radius-lg);
}
.fva-segmented-btn {
  border: none; cursor: pointer; background: none;
  padding: 6px 16px; border-radius: calc(var(--fv-radius-lg) - 2px);
  font: 700 12px/1 "Inter", sans-serif; color: var(--fv-text-secondary);
  transition: background .12s ease, color .12s ease;
}
.fva-segmented-btn.is-active { background: var(--fv-primary); color: var(--fv-on-primary); }

.fva-publish-grid {
  display: grid; grid-template-columns: 2fr 1fr; gap: 16px;
  align-items: start;
}
.fva-publish-side { display: flex; flex-direction: column; gap: 16px; }
.fva-publish-embed { display: flex; flex-direction: column; gap: 20px; }
.fva-publish-embed-head { display: flex; justify-content: space-between; align-items: center; }
.fva-publish-embed-head-left { display: flex; align-items: center; gap: 8px; }
.fva-publish-embed-icon { width: 20px; height: 20px; color: var(--fv-primary); }
.fva-publish-h3 { font-size: 18px; font-weight: 600; margin: 0; }
.fva-tag {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em;
  color: var(--fv-text-muted); background: var(--fv-surface-container);
  padding: 3px 8px; border-radius: var(--fv-radius-sm);
}
.fva-snippet-box { position: relative; }
.fva-snippet-copy-btn {
  position: absolute; right: 10px; top: 10px; z-index: 2;
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--fv-primary-container); color: var(--fv-on-primary-container);
  border: none; cursor: pointer;
  padding: 6px 12px; border-radius: var(--fv-radius-sm);
  font: 700 11px/1 "Inter", sans-serif;
  transition: background .12s ease;
}
.fva-snippet-copy-btn:hover { background: var(--fv-primary); color: var(--fv-on-primary); }
.fva-snippet-copy-btn svg { width: 14px; height: 14px; }
.fva-publish-hint-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.fva-publish-hint {
  background: var(--fv-surface-container-low); border: 1px solid var(--fv-outline-variant);
  border-radius: var(--fv-radius-lg); padding: 14px;
}
.fva-publish-hint-label { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--fv-text-muted); display: block; margin-bottom: 6px; }
.fva-publish-hint-body { font-size: 13px; color: var(--fv-on-surface-variant); margin: 0; line-height: 1.45; }

.fva-publish-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--fv-text-muted); margin: 0 0 16px; }

.fva-integ-row {
  display: flex; justify-content: space-between; align-items: center;
  background: var(--fv-surface-container-low); border-radius: var(--fv-radius-lg);
  padding: 12px; margin-bottom: 12px;
}
.fva-integ-row-off { opacity: .6; }
.fva-integ-row-left { display: flex; align-items: center; gap: 10px; }
.fva-integ-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.fva-integ-dot-on { background: var(--fv-primary); animation: fva-pulse 2s infinite; }
.fva-integ-dot-off { background: var(--fv-outline); }
.fva-integ-name { font-size: 13px; font-weight: 600; }
.fva-integ-check { width: 20px; height: 20px; color: var(--fv-primary); }
.fva-integ-status { font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--fv-text-muted); }
@keyframes fva-pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }

.fva-publish-lift {
  background: var(--fv-primary-container); color: var(--fv-on-primary-container);
  border-radius: var(--fv-radius-xl); padding: 20px; position: relative; overflow: hidden;
}
.fva-publish-lift-bg { position: absolute; right: -12px; bottom: -12px; width: 96px; height: 96px; opacity: .1; }
.fva-publish-lift-body { position: relative; z-index: 1; }
.fva-publish-lift-valrow { display: flex; align-items: baseline; gap: 8px; margin-bottom: 12px; }
.fva-publish-lift-value { font: 700 32px/1 "JetBrains Mono", ui-monospace, monospace; }
.fva-publish-lift-sub { font-size: 13px; opacity: .9; }
.fva-publish-lift-note { font-size: 12px; line-height: 1.5; opacity: .8; margin: 0; }

.fva-publish-reveal { grid-column: 1 / -1; }
.fva-publish-reveal-head { display: flex; align-items: center; gap: 10px; margin-bottom: 24px; }
.fva-publish-reveal-icon { width: 22px; height: 22px; color: var(--fv-primary); }
.fva-publish-steps { display: flex; flex-direction: column; gap: 24px; }
.fva-publish-step { display: flex; gap: 16px; }
.fva-publish-step-badge {
  flex: 0 0 auto; width: 32px; height: 32px; border-radius: 50%;
  background: var(--fv-surface-container-highest); color: var(--fv-text);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 14px;
}
.fva-publish-step-title { font-size: 14px; font-weight: 700; margin: 0 0 4px; }
.fva-publish-step-body { font-size: 13px; color: var(--fv-on-surface-variant); margin: 0; line-height: 1.5; }
.fva-inline-code {
  background: var(--fv-surface-container-high); color: var(--fv-primary);
  font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 12px;
  padding: 2px 6px; border-radius: var(--fv-radius-sm); font-weight: 700;
}

/* ============ INTEGRATIONS / CONNECTION SCREEN (FUL-254) ============ */
.fva-integ-grid {
  display: grid; grid-template-columns: 2fr 1fr; gap: 16px;
  align-items: start;
}
.fva-integ-tabs { margin-bottom: 20px; }
.fva-integ-section-head { margin-bottom: 16px; }
.fva-integ-section-head h3 { margin: 0 0 4px; font-size: 18px; }
.fva-integ-section-head p { margin: 0; color: var(--fv-on-surface-variant); font-size: 13px; }
.fva-webhook-panel { min-width: 0; }
.fva-webhook-grid {
  display: grid; grid-template-columns: minmax(300px, .9fr) minmax(360px, 1.1fr);
  gap: 16px; align-items: start;
}
.fva-webhook-events { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; }
.fva-webhook-list { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.fva-webhook-empty { min-height: 240px; }
.fva-webhook-endpoint { display: flex; flex-direction: column; gap: 14px; }
.fva-webhook-url { display: block; margin-top: 3px; overflow-wrap: anywhere; }
.fva-integ-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.fva-integ-side { display: flex; flex-direction: column; gap: 16px; }
.fva-integ-empty { margin-top: 8px; }

.fva-badge-error { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
.fva-badge-revoked { background: var(--fv-surface-container-high); color: var(--fv-text-muted); border: 1px solid var(--fv-outline-variant); }

.fva-success-banner {
  display: flex; align-items: center; gap: 8px;
  background: #d1fae5; border: 1px solid #a7f3d0;
  border-radius: var(--fv-radius-sm);
  padding: 10px 14px; color: #065f46;
  font-size: 13px; margin-bottom: 16px;
}
.fva-success-banner svg { width: 18px; height: 18px; flex: 0 0 auto; color: var(--fv-success); }

.fva-integ-conn { display: flex; flex-direction: column; gap: 14px; }
.fva-integ-conn-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.fva-integ-conn-head-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.fva-integ-conn-title { margin: 0; font-size: 15px; font-weight: 600; }
.fva-integ-dot-err { background: var(--fv-error); }

.fva-integ-conn-meta {
  display: flex; flex-wrap: wrap; gap: 16px;
  background: var(--fv-surface-container-low);
  border-radius: var(--fv-radius-sm);
  padding: 10px 14px;
}
.fva-integ-meta-cell { display: flex; flex-direction: column; gap: 2px; }
.fva-integ-meta-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--fv-text-muted); }
.fva-integ-meta-value { font-size: 13px; color: var(--fv-text); word-break: break-all; }

.fva-integ-scopes .fva-label { display: block; margin-bottom: 8px; }

.fva-scope-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fva-scope-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font: 600 12px "JetBrains Mono", ui-monospace, monospace;
  padding: 4px 10px; border-radius: 999px;
  border: 1px solid var(--fv-outline-variant);
  background: var(--fv-surface);
  color: var(--fv-text-secondary);
}
.fva-scope-chip-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; }
.fva-scope-chip.is-ok { color: #065f46; border-color: #a7f3d0; background: #ecfdf5; }
.fva-scope-chip.is-ok .fva-scope-chip-dot { background: var(--fv-success); }
.fva-scope-chip.is-missing { color: #991b1b; border-color: #fecaca; background: #fef2f2; }
.fva-scope-chip.is-missing .fva-scope-chip-dot { background: var(--fv-error); }

.fva-integ-scope-help { display: block; }
.fva-scope-missing-list { margin: 8px 0 0; padding-left: 18px; font-size: 13px; }

.fva-integ-conn-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fva-integ-conn-actions .fva-vsl-meta { flex: 1 1 auto; min-width: 0; }

.fva-integ-audit .fva-event-log { max-height: 160px; }

/* ============ RESPONSIVE ============ */
@media (max-width: 860px) {
  .fva-sidebar {
    position: fixed; top: 0; left: 0; z-index: 100;
    transform: translateX(-100%);
    transition: transform .2s ease;
    box-shadow: var(--fv-shadow-2);
  }
  .fva-sidebar.is-open { transform: translateX(0); }
  .fva-menu-btn { display: inline-flex; align-items: center; justify-content: center; }
  .fva-content { padding: 16px; }
  .fva-preview-layout { flex-direction: column; }
  .fva-kpi-row { grid-template-columns: 1fr; }
  .fva-topbar-search { display: none; }
  .fva-insight-grid { grid-template-columns: 1fr; }
  .fva-publish-grid { grid-template-columns: 1fr; }
  .fva-publish-hint-row { grid-template-columns: 1fr; }
  .fva-integ-grid { grid-template-columns: 1fr; }
  .fva-webhook-grid { grid-template-columns: 1fr; }
  .fva-an-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .fva-an-main-grid, .fva-an-mix-grid { grid-template-columns: 1fr; }
  .fv-grid > .fv-card-wide:nth-child(1),
  .fv-grid > .fv-bd-col:nth-child(2) { grid-column: span 12; }
}
@media (max-width: 480px) {
  .fva-vsl-grid { grid-template-columns: 1fr; }
  .fva-field-row { flex-direction: column; }
  .fva-webhook-events { grid-template-columns: 1fr; }
  .fva-topbar { padding: 0 12px; }
  .fva-page-header { flex-direction: column; align-items: flex-start; }
  .fva-an-header-actions { width: 100%; align-items: flex-end; }
  .fva-an-period { flex: 1 1 auto; }
  .fva-an-period .fva-select { width: 100%; }
  .fva-an-kpis { grid-template-columns: 1fr; }
  .fva-an-legend { justify-content: flex-start; margin: 10px 0 0; }
  .fva-an-device-body { align-items: stretch; flex-direction: column; }
  .fva-an-donut { align-self: center; }
  .fva-an-detail-toolbar { align-items: stretch; flex-direction: column; }
  .fva-an-detail-actions { display: grid; grid-template-columns: 1fr 1fr; }
  .fv-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .fv-grid-two { grid-template-columns: 1fr; }
  .fv-resume-grid { grid-template-columns: 1fr; }
  .fv-legend { width: 100%; }
}
.fva-sidebar-backdrop {
  display: none;
  position: fixed; inset: 0; z-index: 90;
  background: rgba(0,0,0,.25);
}
.fva-sidebar-backdrop.is-visible { display: block; }

/* ============ TEMPLATES / GENERIC CARD GRID ============ */
.fva-tpl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}
.fva-tpl-card { display: flex; flex-direction: column; gap: 14px; }
.fva-tpl-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.fva-tpl-title { margin: 0; font-size: 16px; font-weight: 600; color: var(--fv-text); }
.fva-tpl-when { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--fv-text-muted); }
.fva-tpl-desc { margin: 0; font-size: 13px; color: var(--fv-on-surface-variant); line-height: 1.45; }
.fva-tpl-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #1c2120 0%, #2a302f 100%);
  border-radius: var(--fv-radius-lg);
  overflow: hidden;
  display: flex;
}
.fva-tpl-preview-play {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  color: rgba(255,255,255,.4); font-size: 28px; pointer-events: none;
}
.fva-tpl-overlay {
  position: absolute; inset: 0;
  display: flex; padding: 16px;
  z-index: 1;
}
.fva-tpl-overlay-panel { display: flex; flex-direction: column; gap: 6px; max-width: 80%; }
.fva-tpl-overlay-text { margin: 0; font-size: 15px; font-weight: 700; line-height: 1.3; }
.fva-tpl-overlay-sub { margin: 0; font-size: 12px; opacity: .85; line-height: 1.35; }
.fva-tpl-overlay-btn {
  align-self: flex-start;
  font: 700 12px/1 "Inter", sans-serif;
  padding: 7px 14px; border-radius: var(--fv-radius-sm);
  margin-top: 2px;
}
.fva-tpl-form { border-top: 1px solid var(--fv-border-light); padding-top: 14px; }
.fva-tpl-meta { display: flex; flex-direction: column; gap: 4px; }
.fva-tpl-meta-label { font-size: 11px; color: var(--fv-text-muted); }
.fva-tpl-preset-note {
  margin: 0; padding-top: 12px; border-top: 1px solid var(--fv-border-light);
  font-size: 12px; line-height: 1.45; color: var(--fv-on-surface-variant);
}

/* ============ PER-VSL PLAYER SETTINGS ============ */
.fva-color-input {
  width: 64px; height: 40px; padding: 3px;
  border: 1px solid var(--fv-outline-variant); border-radius: var(--fv-radius-sm);
  background: var(--fv-surface); cursor: pointer;
}
.fva-check-row {
  min-height: 40px; display: flex; align-items: center; gap: 9px;
  color: var(--fv-on-surface); font-size: 14px; cursor: pointer;
}
.fva-check-row input { width: 16px; height: 16px; accent-color: var(--fv-primary); }

/* ============ SUPPORT ============ */
.fva-support-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.fva-support-list-item { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--fv-on-surface-variant); line-height: 1.45; }
.fva-support-check { width: 18px; height: 18px; color: var(--fv-primary); flex: 0 0 auto; margin-top: 1px; }
.fva-support-note-head { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
`;
